// src/autodesk_usage/components/shared/chart/SalesVsUsageChart.tsx
// Hover/touch tooltip with smart positioning (follows cursor and never overflows).
// - Keeps popup behavior (do NOT remove).
// - Bars start from zero; time-based X scale; legend + interpretation panel kept.
// - All comments in English per project rule.

import React, { useMemo, useRef, useState, useEffect, useLayoutEffect } from "react";

type AnyRecord = Record<string, any>;

type Props = {
  data: AnyRecord[] | AnyRecord | null | undefined;
  xKey?: string;
  seatsKey?: string;
  usageKey?: string;
  className?: string;
};

function toArray(data: any): AnyRecord[] {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray((data as any).series)) return (data as any).series;
  return [];
}

function detectFieldKey(
  arr: AnyRecord[],
  candidates: string[],
  regexes: RegExp[],
  fallback: string
) {
  for (const k of candidates) {
    if (arr.some((p) => p && p[k] !== undefined)) return k;
  }
  for (const p of arr) {
    if (!p || typeof p !== "object") continue;
    for (const key of Object.keys(p)) {
      const low = key.toLowerCase();
      if (regexes.some((re) => re.test(low))) return key;
    }
  }
  return fallback;
}

/** Normalize to 'YYYY-MM' for strings like '2025-1', '2025/10', '2025-10-01'. */
function formatMonth(x: string | number | Date) {
  if (x instanceof Date) {
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  }
  if (typeof x === "string") {
    const s = x.replace("/", "-");
    const m = s.match(/^(\d{4})-(\d{1,2})/);
    if (m) {
      const y = m[1];
      const mm = String(Number(m[2])).padStart(2, "0");
      return `${y}-${mm}`;
    }
    if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);
    return s;
  }
  return String(x);
}

export default function SalesVsUsageChart({
  data,
  xKey,
  seatsKey,
  usageKey,
  className,
}: Props) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const chartRef = useRef<HTMLDivElement | null>(null);

  // Hovered index for tooltip content
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  // Track mouse/touch position relative to chart container (for tooltip placement)
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);

  // Tooltip DOM size (measured) for clamping
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [tipSize, setTipSize] = useState<{ w: number; h: number }>({ w: 220, h: 120 });

  // Re-measure tooltip size whenever content changes (e.g., different numbers width)
  useLayoutEffect(() => {
    if (!tipRef.current) return;
    const el = tipRef.current;
    // Use rAF to ensure measurements after layout
    const id = requestAnimationFrame(() => {
      setTipSize({
        w: el.offsetWidth || 220,
        h: el.offsetHeight || 120,
      });
    });
    return () => cancelAnimationFrame(id);
  }, [hoverIdx]);

  const raw = toArray(data);

  const realXKey = useMemo(
    () =>
      xKey ??
      detectFieldKey(
        raw,
        ["month", "as_of_month", "date", "period", "ym", "month_label"],
        [/(^|_)(month|as_of_month|date|period|ym)(_|$)/i],
        "month"
      ),
    [raw, xKey]
  );

  const realSeatsKey = useMemo(
    () =>
      seatsKey ??
      detectFieldKey(
        raw,
        ["active_seats", "total_active_seats", "seats", "sales", "total_seats"],
        [/(active.*seat|seat.*active|^seats?$|^sales?$|total.*seats?)/i],
        "active_seats"
      ),
    [raw, seatsKey]
  );

  const realUsageKey = useMemo(
    () =>
      usageKey ??
      detectFieldKey(
        raw,
        ["used_users", "total_used_users", "usage", "users", "total_users"],
        [/(used.*user|user.*used|^users?$|^usage$|total.*users?)/i],
        "used_users"
      ),
    [raw, usageKey]
  );

  const series = useMemo(() => {
    const arr = raw.map((d) => {
      const lbl = d?.[realXKey];
      const seats = Number(d?.[realSeatsKey] ?? 0);
      const usage = Number(d?.[realUsageKey] ?? 0);
      const formatted = formatMonth(lbl);
      const t = Date.parse(`${formatted}-01T00:00:00`);
      return {
        label: formatted,
        seats,
        usage,
        overuse: Math.max(0, usage - seats),
        time: isNaN(t) ? null : t,
      };
    });
    const filtered = arr.filter((p) => p.time != null).sort((a, b) => (a.time! - b.time!));
    return filtered;
  }, [raw, realXKey, realSeatsKey, realUsageKey]);

  if (series.length === 0) {
    return (
      <div className="flex h-[260px] items-center justify-center rounded-xl border bg-white text-gray-500">
        No data available.
      </div>
    );
  }

  // Fixed viewBox for stable scaling
  const W = 980;
  const H = 360;
  const margin = { top: 24, right: 16, bottom: 60, left: 60 };
  const innerW = W - margin.left - margin.right;
  const innerH = H - margin.top - margin.bottom;

  // X (time) scale with clamp
  const minTime = series[0].time!;
  const maxTime = series[series.length - 1].time!;
  const xRaw = (t: number) =>
    margin.left + ((t - minTime) / Math.max(1, maxTime - minTime)) * innerW;
  const clampX = (x: number) =>
    Math.max(margin.left, Math.min(W - margin.right, x));
  const xScale = (t: number) => clampX(xRaw(t));

  // Y scale
  let maxVal = 0;
  for (const p of series) maxVal = Math.max(maxVal, p.seats, p.usage, p.overuse);
  const maxY = Math.max(1, Math.ceil(maxVal * 1.1));
  const yScale = (v: number) => innerH - (v / maxY) * innerH;

  // Paths
  const path = (key: "seats" | "usage") =>
    series
      .map((p, i) => {
        const x = xScale(p.time!);
        const y = margin.top + yScale(p[key]);
        return `${i === 0 ? "M" : "L"} ${x} ${y}`;
      })
      .join(" ");

  // Resolve index by X only (robust over any spacing)
function idxByMouseX(clientX: number) {
  const svg = svgRef.current;
  if (!svg) return null;

  const rect = svg.getBoundingClientRect();

  // 1) Convert client pixel -> SVG viewBox coordinate
  // viewBox width = W (e.g., 980); rendered width = rect.width
  const scaleX = W / rect.width;
  const pxClient = clientX - rect.left;      // in CSS pixels
  const pxSvg = pxClient * scaleX;           // in viewBox units

  // 2) Clamp within chart inner area (also in viewBox units)
  const cx = clampX(pxSvg);

  // 3) Map to time domain
  const ratio = (cx - margin.left) / innerW;
  const t = minTime + ratio * (maxTime - minTime);

  // 4) Pick the NEAREST point (robust vs. boundary)
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < series.length; i++) {
    const xi = xScale(series[i].time!);
    const d = Math.abs(xi - cx);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

  // Convenience: store cursor position relative to chart container for tooltip
  function updateCursorFromEvent(clientX: number, clientY: number) {
    const container = chartRef.current;
    if (!container) return;
    const box = container.getBoundingClientRect();
    const x = clientX - box.left;
    const y = clientY - box.top;
    setCursor({ x, y });
  }

  function onMouseMove(e: React.MouseEvent<SVGSVGElement>) {
    const idx = idxByMouseX(e.clientX);
    if (idx == null) return;
    setHoverIdx(idx);
    updateCursorFromEvent(e.clientX, e.clientY);
  }
  function onMouseLeave() {
    setHoverIdx(null);
    setCursor(null);
  }
  function onTouchStart(e: React.TouchEvent<SVGSVGElement>) {
    if (e.touches?.[0]) {
      const t = e.touches[0];
      const idx = idxByMouseX(t.clientX);
      if (idx != null) setHoverIdx(idx);
      updateCursorFromEvent(t.clientX, t.clientY);
    }
  }
  function onTouchMove(e: React.TouchEvent<SVGSVGElement>) {
    if (e.touches?.[0]) {
      const t = e.touches[0];
      const idx = idxByMouseX(t.clientX);
      if (idx != null) setHoverIdx(idx);
      updateCursorFromEvent(t.clientX, t.clientY);
    }
  }
  function onTouchEnd() {
    setHoverIdx(null);
    setCursor(null);
  }

  const tip = hoverIdx != null ? series[hoverIdx] : null;

  // Compute tooltip top/left with clamping inside container
  const tipOffset = { x: 14, y: 12 }; // small gap from cursor
  const [tipPos, setTipPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });

  useEffect(() => {
    if (!chartRef.current || !cursor || !tip) return;
    const container = chartRef.current;
    const cw = container.clientWidth;
    const ch = container.clientHeight;

    // Desired position near cursor
    let left = cursor.x + tipOffset.x;
    let top = cursor.y + tipOffset.y;

    // Clamp within bounds (8px padding)
    const pad = 8;
    const maxLeft = cw - tipSize.w - pad;
    const maxTop = ch - tipSize.h - pad;
    left = Math.max(pad, Math.min(maxLeft, left));
    top = Math.max(pad, Math.min(maxTop, top));

    setTipPos({ left, top });
  }, [cursor, tip, tipSize]);

  // Label spacing
  const xLabelStep = series.length > 12 ? Math.ceil(series.length / 12) : 1;
  const rotateLabels = series.length > 10;

  return (
    <div className={["w-full", className].filter(Boolean).join(" ")}>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr,320px]">
        {/* Chart + smart tooltip */}
        <div ref={chartRef} className="relative rounded-xl border bg-white p-3">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            className="h-[320px] w-full"
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {/* Y-axis */}
            <line
              x1={margin.left}
              y1={margin.top}
              x2={margin.left}
              y2={H - margin.bottom}
              stroke="currentColor"
              className="text-gray-300"
              strokeWidth={1}
            />
            {/* Y ticks & labels */}
            {Array.from({ length: 5 }).map((_, i) => {
              const val = (maxY / 4) * (4 - i);
              const y = margin.top + (innerH / 4) * i;
              return (
                <g key={i}>
                  <line
                    x1={margin.left - 4}
                    x2={margin.left}
                    y1={y}
                    y2={y}
                    stroke="currentColor"
                    className="text-gray-400"
                  />
                  <text
                    x={margin.left - 8}
                    y={y + 4}
                    textAnchor="end"
                    className="fill-gray-600 text-[11px]"
                  >
                    {Math.round(val)}
                  </text>
                </g>
              );
            })}
            {/* Horizontal grid */}
            {Array.from({ length: 5 }).map((_, i) => {
              const y = margin.top + (innerH / 4) * i;
              return (
                <line
                  key={`grid-${i}`}
                  x1={margin.left}
                  x2={W - margin.right}
                  y1={y}
                  y2={y}
                  stroke="currentColor"
                  className="text-gray-200"
                  strokeDasharray="4 4"
                />
              );
            })}

            {/* Overuse bars from zero */}
            {series.map((p, i) => {
              if (p.overuse <= 0) return null;
              const xc = xScale(p.time!);
              const yZero = margin.top + yScale(0);
              const yTop = margin.top + yScale(p.overuse);
              return (
                <rect
                  key={i}
                  x={xc - 6}
                  y={yTop}
                  width={12}
                  height={yZero - yTop}
                  className="fill-red-400/30"
                  rx={2}
                />
              );
            })}

            {/* Lines */}
            <path d={path("seats")} className="fill-none stroke-blue-600" strokeWidth={2} />
            <path d={path("usage")} className="fill-none stroke-red-500" strokeWidth={2} />

            {/* Points */}
            {series.map((p, i) => {
              const x = xScale(p.time!);
              const yS = margin.top + yScale(p.seats);
              const yU = margin.top + yScale(p.usage);
              const r = hoverIdx === i ? 4 : 3;
              return (
                <g key={i}>
                  <circle cx={x} cy={yS} r={r} className="fill-blue-600" />
                  <circle cx={x} cy={yU} r={r} className="fill-red-500" />
                </g>
              );
            })}

            {/* X-axis labels */}
            {series.map((p, i) => {
              if (i % xLabelStep !== 0 && i !== series.length - 1) return null;
              const x = xScale(p.time!);
              const y = H - margin.bottom + 20;
              const rotate = rotateLabels ? -30 : 0;
              const anchor = rotateLabels
                ? "end"
                : i === 0
                ? "start"
                : i === series.length - 1
                ? "end"
                : "middle";
              return (
                <text
                  key={i}
                  x={x}
                  y={y}
                  transform={`rotate(${rotate}, ${x}, ${y})`}
                  textAnchor={anchor as any}
                  className="fill-gray-600 text-[12px]"
                >
                  {p.label}
                </text>
              );
            })}
          </svg>

          {/* Smart-positioned tooltip (follows cursor; clamped inside) */}
          {tip && cursor && (
            <div
              ref={tipRef}
              className="pointer-events-none absolute z-10 max-w-[260px] rounded-lg border bg-white/95 p-3 text-xs shadow"
              style={{ left: tipPos.left, top: tipPos.top }}
            >
              <div className="mb-1 font-medium">{tip.label}</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-3 rounded-sm bg-blue-600" />
                  <span>Active Seats</span>
                </div>
                <div className="text-right tabular-nums">{tip.seats}</div>

                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-3 rounded-sm bg-red-500" />
                  <span>Used Users</span>
                </div>
                <div className="text-right tabular-nums">{tip.usage}</div>

                <div className="flex items-center gap-2">
                  <span className="inline-block h-2 w-3 rounded-sm bg-red-400/30 ring-1 ring-red-300/50" />
                  <span>Overuse Gap</span>
                </div>
                <div className="text-right tabular-nums">
                  {Math.max(0, tip.usage - tip.seats)}
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="mt-2 flex flex-wrap items-center gap-4 px-1 text-sm">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-3 rounded-sm bg-blue-600" />
              <span>Total Active Seats</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-3 rounded-sm bg-red-500" />
              <span>Total Used Users</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-3 rounded-sm bg-red-400/30 ring-1 ring-red-300/50" />
              <span>Overuse Gap</span>
            </div>
          </div>
        </div>

        {/* Interpretation panel */}
        <div className="rounded-xl border bg-white p-4">
          <h3 className="mb-2 text-base font-semibold text-indigo-900">How to interpret</h3>
          <ul className="space-y-2 text-sm leading-6 text-gray-700">
            <li>
              <span className="font-medium text-blue-600">●</span> Active Seats: Number of entitled
              licenses.
            </li>
            <li>
              <span className="font-medium text-red-500">●</span> Used Users: Number of distinct users
              observed.
            </li>
            <li>
              <span className="font-medium text-rose-400">●</span> Overuse Gap:
              <code className="ml-1 rounded bg-gray-100 px-1 font-mono text-xs">
                overuse = max(0, used_users − active_seats)
              </code>
            </li>
            <li>
              <span className="font-medium text-emerald-500">●</span> Overuse %:
              <code className="ml-1 rounded bg-gray-100 px-1 font-mono text-xs">
                (used / seats − 1) × 100%
              </code>
            </li>
            <li>
              When <span className="text-red-600 font-medium">red line</span> stays above{" "}
              <span className="text-blue-600 font-medium">blue line</span>, it indicates persistent
              overuse — potential upsell opportunity or competitor risk.
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
